import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { ReconciliationSupplierOrder, SaveOrderAdjustmentPayload } from './types';

const getEndpoint = (orderId: number) => `ums/v1/cms/reconciliation/orders/${orderId}/adjustment`;

type TData = ReconciliationSupplierOrder;
type TError = AxiosError;
type TVariables = SaveOrderAdjustmentPayload;
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/** Records the finance-side overlay for one order (adjusted lines, cancelled lines, typed total) —
 * the platform order is never modified. 409 when the month is closed. Returns the refreshed
 * orders-modal row (wrapped in `{ data }` like the list endpoint). */
export function useSaveOrderAdjustmentApi(options?: Options): ReturnType {
  const toast = useCustomToast();

  const mutationFn = async ({ orderId, items, totalOverride, reason }: TVariables) => {
    try {
      const { data } = await http.put(getEndpoint(orderId), { items, totalOverride: totalOverride ?? null, reason });
      return data?.data ?? data;
    } catch (error: any) {
      if (!error.response?.status) throw error;

      toast({
        description: error.response?.data?.message ?? 'Failed to save the order adjustment',
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, { ...options, mutationKey: ['reconciliation-save-order-adjustment'] });
}
